import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMsal } from "@azure/msal-react";

import { CustomView } from '../../../../api-client';
import { IHookCallback } from '../../../../common/models';
import { CustomViewApiClient } from '../../../../providers/api-provider';
import { NotificationHelper } from '../../../../utils/notification';
import { useSystem } from '../../../../common/hooks/useSystem';

export const useCustomView = () => {
    const {
        instance
    } = useMsal();

    const {
        t: translate
    } = useTranslation();

    const [customViews, setCustomViews] = useState<CustomView[]>();
    const [customViewsLoading, setCustomViewsLoading] = useState<boolean>(false);

    const handleCustomView = async () => {
        setCustomViewsLoading(true);

        const clientApi = await CustomViewApiClient(instance);

        clientApi.getCustomViews()
            .then((response) => {
                setCustomViews(response.data);
            })
            .catch((reason) => {
                setCustomViews([]);

                NotificationHelper.createNotification({
                    type: 'error',
                    title: translate('settingPage.customViewPage.notification.retrieveAll.title'),
                    message: translate('settingPage.customViewPage.notification.retrieveAll.message')
                });
            })
            .finally(() => {
                setCustomViewsLoading(false);
            });
    }

    // CRUD Operations

    const {
        handleSystemCustomViews
    } = useSystem();

    interface ICreateCustomView extends IHookCallback {
        customView: CustomView;
    }

    const [createCustomViewLoading, setCreateCustomViewLoading] = useState<boolean>(false);

    const handleCreateCustomView = async (params: ICreateCustomView) => {
        setCreateCustomViewLoading(true);
        
        const clientApi = await CustomViewApiClient(instance);

        clientApi.insertCustomView(params.customView)
            .then((response) => {
                handleSystemCustomViews();

                params.onSuccess && params.onSuccess();

                NotificationHelper.createNotification({
                    type: 'success',
                    title: translate('settingPage.customViewPage.notification.createSuccess.title'),
                    message: translate('settingPage.customViewPage.notification.createSuccess.message')
                });
            })
            .catch((reason) => {
                params.onError && params.onError();

                NotificationHelper.createNotification({
                    type: 'error',
                    title: translate('settingPage.customViewPage.notification.createError.title'),
                    message: translate('settingPage.customViewPage.notification.createError.message')
                });
            })
            .finally(() => {
                setCreateCustomViewLoading(false);

                params.onAlways && params.onAlways();
            });
    }

    interface IUpdateCustomView extends IHookCallback {
        id: string;
        customView: CustomView;
    }

    const [updateCustomViewLoading, setUpdateCustomViewLoading] = useState<boolean>(false);

    const handleUpdateCustomView = async (params: IUpdateCustomView) => {
        setUpdateCustomViewLoading(true);

        const clientApi = await CustomViewApiClient(instance);

        clientApi.updateCustomView(params.id, params.customView)
            .then((response) => {
                handleSystemCustomViews();

                params.onSuccess && params.onSuccess();

                NotificationHelper.createNotification({
                    type: 'success',
                    title: translate('settingPage.customViewPage.notification.updateSuccess.title'),
                    message: translate('settingPage.customViewPage.notification.updateSuccess.message')
                });
            })
            .catch((reason) => {
                params.onError && params.onError();

                NotificationHelper.createNotification({
                    type: 'error',
                    title: translate('settingPage.customViewPage.notification.updateError.title'),
                    message: translate('settingPage.customViewPage.notification.updateError.message')
                });
            })
            .finally(() => {
                setUpdateCustomViewLoading(false);

                params.onAlways && params.onAlways();
            });
    }

    interface IDeleteCustomView extends IHookCallback {
        id: string;
    }

    const [deleteCustomViewLoading, setDeleteCustomViewLoading] = useState<boolean>(false);

    const handleDeleteCustomView = async (params: IDeleteCustomView) => {
        setDeleteCustomViewLoading(true);

        const clientApi = await CustomViewApiClient(instance);

        clientApi.deleteCustomView(params.id)
            .then((response) => {
                handleSystemCustomViews();

                params.onSuccess && params.onSuccess();

                NotificationHelper.createNotification({
                    type: 'success',
                    title: translate('settingPage.customViewPage.notification.deleteSuccess.title'),
                    message: translate('settingPage.customViewPage.notification.deleteSuccess.message')
                });
            })
            .catch((reason) => {
                params.onError && params.onError();

                NotificationHelper.createNotification({
                    type: 'error',
                    title: translate('settingPage.customViewPage.notification.deleteError.title'),
                    message: translate('settingPage.customViewPage.notification.deleteError.message')
                });
            })
            .finally(() => {
                setDeleteCustomViewLoading(false);

                params.onAlways && params.onAlways();
            });
    }

    return {
        handleCustomView,
        customViews,
        customViewsLoading,
        handleCreateCustomView,
        createCustomViewLoading,
        handleUpdateCustomView,
        updateCustomViewLoading,
        handleDeleteCustomView,
        deleteCustomViewLoading
    }
}